import css from "./BookingInfo.module.scss";

import React, { useEffect, useMemo, useState } from "react";
import {
  DatePeriod,
  formatArrivalDate,
  isNewYearsEve,
  TimeOfDay,
} from "@greeter/date";
import { Booking, Order, OrderLine, Table } from "@greeter/core";
import { classNames, entries } from "@greeter/util";
import { IonIcon } from "@ionic/react";
import {
  cashOutline,
  ellipseOutline,
  peopleOutline,
  pricetagOutline,
  squareOutline,
  tabletLandscapeOutline,
  timeOutline,
  watchOutline,
} from "ionicons/icons";

import { match } from "ts-pattern";
import { BookingId } from "@greeter/matter";
import { formatMoney } from "@greeter/formatters";
import { set } from "zod";
import { subDays } from "date-fns";

const INDENT = "  ";
function countIndents(s: string) {
  let indents = 0;
  const split = s.split(INDENT);
  for (const s of split) {
    if (s !== "") break;

    indents++;
  }

  return indents;
}

const dateFormatter = new Intl.DateTimeFormat("da-DK", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

type CommentProps = {
  comment: string;
};
function Comment(props: CommentProps) {
  return (
    <div>
      <h4>Kommentar</h4>
      <pre style={{ color: "white" }}>{props.comment}</pre>
    </div>
  );
}

function DashedLine() {
  return (
    <div
      style={{
        borderBottomStyle: "dashed",
        borderBottomWidth: "1px",
        opacity: 0.3,
      }}
    />
  );
}

function OrderLineInfo(props: { orderLine: OrderLine; level: number }) {
  return (
    <p
      style={{
        // marginLeft: 5 * props.level,
        fontSize: 20 - (props.level + 1) * 2,
        margin: 0,
      }}
    >
      {!props.orderLine.productId && props.orderLine.quantity === 1
        ? props.orderLine.title
        : `${props.orderLine.quantity} x ${props.orderLine.title}`}
    </p>
  );
}

function depth(orderLine: OrderLine, level: number = 0): number {
  if (orderLine.children.length === 0) return level;
  for (const child of orderLine.children) {
    return depth(child, level + 1);
  }
  return level;
}

function OrderLinesInfo(props: {
  orderLines: Array<OrderLine>;
  level: number;
}) {
  return (
    <div>
      {props.orderLines.map((ol) => {
        return (
          <div
            style={{
              marginTop: props.level === 1 ? 5 : 0,
              marginBottom: props.level === 1 ? 5 : 0,
              marginLeft: props.level * 7.5,
            }}
          >
            <OrderLineInfo orderLine={ol} level={props.level} />
            <OrderLinesInfo orderLines={ol.children} level={props.level + 1} />
          </div>
        );
      })}
    </div>
  );
}

export function mapTableInfo(bookingId: string, tables: Map<string, Table[]>) {
  return entries(tables).map(([area, tables]) => {
    const key = `${bookingId}-${area}-${tables
      .map((t: Table) => t.number)
      .join("-")}`;

    const commaSepTables = tables.map((t: Table) => t.number).join(", ");

    return { key: key, area, commaSepTables };
  });
}

export type BookingInfoProps = {
  booking: Booking;
};

const DEPRECATED_ORDER_DEPTH = 0;

export function BookingInfo({ booking }: BookingInfoProps) {
  const { order, period } = booking;

  // Layer depth of 0 is the old setup
  // Layer depth of 2 (or 1) is the new setup.
  //
  // The reason being that the old way was not many layers
  // rather a bundle would be summed into the description of the order line.
  const layerDepth = useMemo(() => {
    let deepest = 0;
    for (const ol of booking.order?.orderLines ?? []) {
      const count = depth(ol);
      if (count > deepest) {
        deepest = count;
      }
    }
    return deepest;
  }, [booking.order]);

  const upsellLines =
    order?.orderLines.filter((ol) => ol.children.length === 0) ?? [];

  return (
    <div className={css.BookingInfo}>
      <div className={css.ShortInfo}>
        <div>
          <div {...classNames(css.InfoHeader)}>
            <IonIcon icon={pricetagOutline} /> Booking id.
          </div>
          <div {...classNames(css.Info)}>
            <BookingId id={booking.id} />
          </div>
        </div>
        <div>
          <div {...classNames(css.InfoHeader)}>
            <IonIcon icon={timeOutline} /> Tidspunkt
          </div>
          <div {...classNames(css.ArrivalDate, css.Info)}>
            <span>{formatArrivalDate(period.from)}</span>
            <br />
            kl. {TimeOfDay.fromDate(booking.period.from).toString()}
          </div>
        </div>
        <div>
          <div {...classNames(css.InfoHeader)}>
            <IonIcon icon={squareOutline} /> Bord
          </div>
          <div {...classNames(css.TableNumber, css.Info)}>
            {mapTableInfo(booking.id, booking.tables).map((r) => {
              return (
                <div key={r.key} id={r.key} data-testid={r.key}>
                  {r.area}: {r.commaSepTables}
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div {...classNames(css.InfoHeader)}>
            <IonIcon icon={peopleOutline} /> Antal personer
          </div>
          <div {...classNames(css.GroupSize, css.Info)}>
            {booking.groupSize} pers.
          </div>
        </div>
        {order && (
          <>
            <div>
              <div {...classNames(css.InfoHeader)}>
                <IonIcon icon={ellipseOutline} /> Ordrestatus
              </div>
              <div {...classNames(css.Info)}>
                {match(booking.order?.status)
                  .with("created", () => <div>Afventer betaling</div>)
                  .with("pending", () => <div>Betaling reserveret</div>)
                  .with("completed", () => <div>Gennemført</div>)
                  .with("cancelled", () => <div>Annulleret</div>)
                  .otherwise((s) => (
                    <div>{s}</div>
                  ))}
              </div>
            </div>
            <div>
              <div {...classNames(css.InfoHeader)}>
                <IonIcon icon={cashOutline} /> Ordresum
              </div>
              {order && (
                <div {...classNames(css.OrderSum, css.OrderInfo)}>
                  {formatMoney(order.total)} kr.
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* TODO: Add quantity for an orderline */}
      <div
        style={{
          borderBottomStyle: "dashed",
          borderBottomWidth: "1px",
          opacity: 0.3,
        }}
      />
      {order && layerDepth > DEPRECATED_ORDER_DEPTH ? (
        <div key={order.id} className={css.TablePackage}>
          <div className={css.Description}>
            <OrderLinesInfo
              orderLines={order.orderLines.filter(
                (ol) => ol.children.length > 0
              )}
              level={0}
            />

            {upsellLines.length > 0 && (
              <h4 style={{ marginTop: 20, marginBottom: 10 }}>Tilkøb</h4>
            )}
            <OrderLinesInfo orderLines={upsellLines} level={0} />
          </div>
        </div>
      ) : order && layerDepth <= DEPRECATED_ORDER_DEPTH ? (
        <div key={order.id} className={css.TablePackage}>
          <div className={css.Description}>
            {order.orderLines
              .filter((ol) => ol.quantity > 0)
              .map((ol) => {
                return (
                  <div key={ol.id} className={css.TablePackage}>
                    <div className={css.Title}>
                      {ol.quantity} x {ol.title}
                    </div>
                    <div className={css.Description}>
                      {ol.description.split("\n").map((l, i) => (
                        <p style={{ margin: 0 }} key={i}>
                          {l}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ) : (
        <div className={css.TablePackage}>Ingen ordre tilknyttet</div>
      )}
      {booking.comment && booking.comment.length > 0 && (
        <>
          <DashedLine />
          <Comment comment={booking.comment} />
        </>
      )}
    </div>
  );
}
